import React from "react";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";

// Section variants
const sectionVariants = cva("w-full relative", {
  variants: {
    spacing: {
      default: "py-16 md:py-24",
      sm: "py-8 md:py-12",
      md: "py-12 md:py-20",
      lg: "py-16 md:py-28",
      xl: "py-24 md:py-36",
      none: "py-0",
    },
    variant: {
      default: "bg-background",
      muted: "bg-muted",
      primary: "bg-primary text-primary-foreground",
      accent: "bg-accent/10",
      dark: "bg-black text-white",
      light: "bg-white text-black",
    },
  },
  defaultVariants: {
    spacing: "default",
    variant: "default",
  },
});

// Section component
const Section = React.forwardRef(
  ({ children, className, id, spacing, variant, ...props }, ref) => {
    return (
      <section
        id={id}
        ref={ref}
        className={cn(sectionVariants({ spacing, variant }), className)}
        {...props}
      >
        {children}
      </section>
    );
  }
);
Section.displayName = "Section";

// Section container component
const SectionContainer = React.forwardRef(
  ({ children, className, size, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("container ", className)} {...props}>
        {children}
      </div>
    );
  }
);
SectionContainer.displayName = "SectionContainer";

// Section header component
const SectionHeader = ({ children, className, centered, ...props }) => {
  return (
    <header
      className={cn(
        "mb-10 space-y-4",
        centered && "text-center mx-auto max-w-3xl",
        className
      )}
      {...props}
    >
      {children}
    </header>
  );
};

// Section title component
const SectionTitle = ({ children, className, ...props }) => {
  return (
    <h2
      className={cn(
        "text-3xl md:text-5xl tracking-tight max-w-xl text-left font-regular",
        className
      )}
      {...props}
    >
      {children}
    </h2>
  );
};

// Section subtitle component
const SectionSubtitle = ({ children, className, ...props }) => {
  return (
    <h3 className={cn("text-xl md:text-2xl font-medium", className)} {...props}>
      {children}
    </h3>
  );
};

// Section description component
const SectionDescription = ({ children, className, ...props }) => {
  return (
    <p
      className={cn(
        "text-lg max-w-xl lg:max-w-lg leading-relaxed tracking-tight text-muted-foreground text-left",
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};

// Section content component
const SectionContent = ({ children, className, ...props }) => {
  return (
    <div className={cn("w-full", className)} {...props}>
      {children}
    </div>
  );
};

export {
  Section,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  SectionDescription,
  SectionContent,
};
